import { Head, useForm } from '@inertiajs/react';
import { useMemo } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import FormInput from '@/Components/FormInput';
import PageHeader from '@/Components/PageHeader';
import { ArrowLeftIcon, UserIcon, IdentificationIcon, MapPinIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

export default function Edit({ employee, locations = [] }) {
    const { data, setData, put, processing, errors } = useForm({
        full_name: employee.full_name || '',
        employee_code: employee.employee_code || '',
        is_active: employee.is_active ?? true,
        work_location_id: employee.work_location_id || '',
        department_id: employee.department_id || '',
    });

    // Filter departments based on selected work location
    const departments = useMemo(() => {
        if (!data.work_location_id) return [];
        const loc = locations.find(l => l.id == data.work_location_id);
        return loc?.departments || [];
    }, [data.work_location_id, locations]);

    const handleLocationChange = (e) => {
        setData(prev => ({
            ...prev,
            work_location_id: e.target.value,
            department_id: '', // Reset department when location changes
        }));
    };

    const submit = (e) => {
        e.preventDefault();
        put(`/employees/${employee.id}`);
    };

    return (
        <AppLayout>
            <Head title="Edit Employee" />
            <PageHeader
                title="Edit Employee"
                subtitle={
                    <span className="flex items-center gap-1.5 text-sm text-slate-500">
                        <a href="/employees" className="text-emerald-700 hover:underline">Employees</a>
                        <span>/</span>
                        <span>Edit {employee.full_name}</span>
                    </span>
                }
                actions={
                    <Button href="/employees" variant="outline" size="sm">
                        <ArrowLeftIcon className="w-4 h-4" /> Back
                    </Button>
                }
            />

            <Card>
                <CardBody>
                    <form onSubmit={submit} className="space-y-6 max-w-lg">
                        <FormInput
                            label="Full Name"
                            icon={UserIcon}
                            value={data.full_name}
                            onChange={e => setData('full_name', e.target.value)}
                            error={errors.full_name}
                            placeholder="Enter employee full name"
                            required
                        />
                        <FormInput
                            label="Employee Code"
                            icon={IdentificationIcon}
                            value={data.employee_code}
                            onChange={e => setData('employee_code', e.target.value)}
                            error={errors.employee_code}
                            placeholder="Enter unique employee code"
                            required
                        />

                        {/* Work Location */}
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                <span className="flex items-center gap-1.5">
                                    <MapPinIcon className="w-4 h-4 text-slate-400" />
                                    Work Location
                                </span>
                            </label>
                            <select
                                value={data.work_location_id}
                                onChange={handleLocationChange}
                                className="w-full rounded-xl border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20 transition px-4 py-2.5"
                            >
                                <option value="">— Select Work Location —</option>
                                {locations.map(loc => (
                                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                                ))}
                            </select>
                            {errors.work_location_id && <p className="mt-1 text-xs text-red-500">{errors.work_location_id}</p>}
                        </div>

                        {/* Department */}
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                <span className="flex items-center gap-1.5">
                                    <BuildingOfficeIcon className="w-4 h-4 text-slate-400" />
                                    Department
                                </span>
                            </label>
                            <select
                                value={data.department_id}
                                onChange={e => setData('department_id', e.target.value)}
                                className="w-full rounded-xl border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20 transition px-4 py-2.5"
                                disabled={!data.work_location_id}
                            >
                                <option value="">
                                    {data.work_location_id
                                        ? (departments.length > 0 ? '— Select Department —' : '— No departments available —')
                                        : '— Select a location first —'
                                    }
                                </option>
                                {departments.map(dept => (
                                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                                ))}
                            </select>
                            {errors.department_id && <p className="mt-1 text-xs text-red-500">{errors.department_id}</p>}
                        </div>

                        <div>
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={data.is_active}
                                    onChange={e => setData('is_active', e.target.checked)}
                                    className="w-4.5 h-4.5 rounded border-slate-300 text-emerald-700 focus:ring-emerald-700/20 focus:ring-2 transition"
                                />
                                <span className="text-sm font-medium text-slate-700">Active</span>
                            </label>
                            <p className="mt-1 text-xs text-slate-400 ml-7.5">
                                Inactive employees will not appear in timesheet selections.
                            </p>
                            {errors.is_active && <p className="mt-1 text-xs text-red-500">{errors.is_active}</p>}
                        </div>
                        <div className="flex gap-3 pt-4">
                            <Button type="submit" disabled={processing}>
                                {processing ? 'Saving...' : 'Save Changes'}
                            </Button>
                            <Button href="/employees" variant="outline">Cancel</Button>
                        </div>
                    </form>
                </CardBody>
            </Card>
        </AppLayout>
    );
}
